import { useMemo } from 'react';

import { maxBy, orderBy } from 'lodash';
import colorsVars from 'styles/colors.module.scss';

import { useThemeStatusStore } from 'store/themeStatus.store';

import { SummaryCard } from 'api/queries/getProjectStatusScoreCards.query';

import { WidgetCard } from 'components/common';
import classes from 'components/widgets/ProjectSummary/components/CardsSection/CardsSection.module.scss';
import { maxHeight, maxValueScale } from 'components/widgets/ProjectSummary/components/CardsSection/CardsSection.utils';
import { CardGraph } from 'components/widgets/ProjectSummary/components/CardsSection/components/CardGraph/CardGraph';
import {
    getTitleColorByStatus,
    serializeData,
    serializeOptions,
} from 'components/widgets/ProjectSummary/components/CardsSection/components/ProgressCard/ProgressCard.utils';
import { ProgressCardMetrics } from 'components/widgets/ProjectSummary/components/CardsSection/components/ProgressCard/ProgressCardMetrics';
import { ToggleCollapse } from 'components/widgets/ProjectSummary/components/ToggleCollapse/ToggleCollapse';

interface Props extends SummaryCard {
    collapse: boolean;
    setCollapse: (value: boolean) => void;
    chartHeight?: number | string;
    canExpand: boolean;
}

export const ProgressCard = ({
    cardProperties,
    cardTitle,
    cardTrends,
    overallScore,
    collapse,
    setCollapse,
    chartHeight,
    canExpand,
}: Props) => {
    const sortedMetricsList = useMemo(() => orderBy(cardProperties.metrics, ['order', 'desc']), [cardProperties]);
    const { white } = colorsVars;
    const showGraphSection = cardTrends?.[0];
    const height = !collapse && showGraphSection ? maxHeight : '100%';
    const maxValuePoint = maxBy(cardTrends?.[0]?.graphPoints, 'score');
    const maxValue = maxValuePoint ? maxValuePoint?.score * maxValueScale : undefined;
    const status = cardTitle.status;
    const tooltipContent =
        status === 'NO DATA' ? 'No data found matching the filter' : `Overall Score is ${overallScore.value}`;
    const id = `statusSummary_progress`;

    const { themeStatus } = useThemeStatusStore();

    return (
        <WidgetCard
            titleColor={
                status === 'NO DATA' ? white : getTitleColorByStatus({ value: overallScore.value, theme: themeStatus })
            }
            titleText={cardTitle.title}
            scoreValue={status}
            contentHeight={height}
            tooltipContent={tooltipContent}
            id={id}
        >
            <div className={classes.cardWrapper}>
                {canExpand && (
                    <div className={classes.topSection}>
                        <div className={`${classes.metricsListContainer} ${collapse && classes.collapse}`}>
                            <ProgressCardMetrics sortedMetricsList={sortedMetricsList} id={id} />
                        </div>
                        <ToggleCollapse setCollapse={setCollapse} collapse={collapse} id={id} />
                    </div>
                )}
                {showGraphSection && (
                    <CardGraph
                        data={cardTrends}
                        xaxisTitle={'Version'}
                        yaxisTitle={'Schedule Performance Index'}
                        yAxisMaxValue={maxValue}
                        height={chartHeight}
                        serializeOptions={serializeOptions}
                        serializeData={serializeData}
                    />
                )}
            </div>
        </WidgetCard>
    );
};
